import type { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/queryClient";
import { capture } from "@/lib/sentry/capture";
import {
  billingBuildingResultSchema,
  billingContextSchema,
  billingInvoicesSchema,
  billingOverviewSchema,
  billingSetupSessionSchema,
  billingPlanCatalogSchema,
  buildingSubscriptionSchema,
  buildingPlanChangeSchema,
  type BuildingPlanTier,
  type BillingDetailsInput,
  type BillingInvoice,
} from "./types";

/**
 * The root of every billing react-query key.
 *
 * It lives here rather than in `hooks.ts` because `requestBillingSync` — called
 * from the buildings repository, outside React — has to invalidate the same
 * cache, and importing the hooks module from here would be a cycle.
 */
export const BILLING_QUERY_KEY = ["organisation-billing"] as const;

/** Codes the `billing` edge function answers refusals with. */
export const BILLING_ERROR_CODES = [
  "not_owner",
  "read_only",
  "details_missing",
  "payment_method_missing",
  "pilot",
  "validation",
  "stripe",
  "plan_limit",
  "free_building_limit",
  "free_account_limit",
  "plan_selection_required",
  "plan_storage_limit",
  "plan_change_busy",
] as const;

export type BillingErrorCode = (typeof BILLING_ERROR_CODES)[number];

/**
 * A refusal from the `billing` edge function.
 *
 * The function answers `{ error: { code, message, field? } }`. Most refusals are
 * a state the UI has its own translated sentence for, keyed on `code`, and the
 * English `message` stays in the console. Two do not: a `validation` refusal and
 * a Stripe one name the specific thing that is wrong — which field, which value
 * Stripe would not take — and no code can carry that, so the message is shown
 * (see `billingErrorMessage`). `field` is the Edge Function's own column name
 * (`vat_number`), so the page can put the message under the input rather than
 * only in a toast.
 */
export class BillingRequestError extends Error {
  readonly code: BillingErrorCode | null;
  /** The payload field the server blamed, in its own snake_case naming. */
  readonly field: string | null;
  readonly details: unknown;

  constructor(
    message: string,
    code: string | null,
    field: string | null = null,
    details: unknown = null
  ) {
    super(message);
    this.name = "BillingRequestError";
    this.code = BILLING_ERROR_CODES.includes(code as BillingErrorCode)
      ? (code as BillingErrorCode)
      : null;
    this.field = field;
    this.details = details;
  }
}

function stringField(body: unknown, key: string): string | null {
  if (!body || typeof body !== "object") return null;
  const value = (body as Record<string, unknown>)[key];
  return typeof value === "string" ? value : null;
}

/** The `{ code, message }` object out of a refusal body, or null. */
async function refusalDetail(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const body: unknown = await response.json();
    const detail = (body as { error?: unknown })?.error;
    return detail && typeof detail === "object" ? (detail as Record<string, unknown>) : null;
  } catch {
    // Non-JSON body (a gateway error page, a truncated response).
    return null;
  }
}

async function toBillingError(error: unknown): Promise<Error> {
  // functions-js reports a non-2xx as a FunctionsHttpError carrying the raw
  // Response; the JSON body is never parsed for us.
  const ctx = (error as { context?: Response })?.context;
  const detail = ctx && typeof ctx.json === "function" ? await refusalDetail(ctx) : null;
  if (detail) {
    return new BillingRequestError(
      stringField(detail, "message") ?? "Billing request refused",
      stringField(detail, "code"),
      stringField(detail, "field"),
      detail.details
    );
  }
  return new BillingRequestError(
    error instanceof Error ? error.message : "Billing request failed",
    null
  );
}

type BillingAction =
  | "save_details"
  | "add_building"
  | "remove_building"
  | "restore_building"
  | "sync"
  | "portal_link"
  | "setup_payment_method"
  | "confirm_setup"
  | "change_building_plan";

async function invokeBilling(
  action: BillingAction,
  orgId: string,
  payload: Record<string, unknown> = {}
): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke("billing", {
    body: { action, org_id: orgId, ...payload },
  });
  if (error) throw await toBillingError(error);
  return data;
}

/**
 * Parse a billing read, naming the drift rather than throwing a raw ZodError.
 *
 * A payload we cannot read is still a payload we must not render — a guessed
 * rate or a missed read-only flag is worse than an error state — so this throws.
 * What it adds is a console line naming the fields that moved, because the
 * alternative is a stack of Zod internals and a blank page with no clue which
 * end drifted. The blast radius is billing's own error state: the permission
 * gate withholds one permission for an unreadable context, not the app's writes.
 */
function parseBillingRead<T>(schema: z.ZodType<T>, data: unknown, what: string): T {
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  const fields = parsed.error.issues.map((issue) => issue.path.join(".") || "(root)").join(", ");
  // The read name and the field list are arguments, never spliced into the
  // format string: `what` is ours but `fields` is derived from a server payload,
  // and a payload key containing a format specifier would be interpreted rather
  // than printed.
  const failure = new Error(`Unexpected ${what} shape from the billing RPC: ${fields}`);
  capture(failure, {
    tags: { feature: "billing", stage: "parse" },
    fingerprint: ["billing", "parse", what],
    context: { billing: { read: what, fields } },
  });
  throw failure;
}

export async function getBillingPlanCatalog() {
  const { data, error } = await supabase.rpc("get_billing_plan_catalog");
  if (error) throw error;
  return parseBillingRead(billingPlanCatalogSchema, data, "plan catalog");
}

export async function getBuildingSubscription(buildingId: number) {
  const { data, error } = await supabase.rpc("get_building_subscription", {
    p_building_id: buildingId,
  });
  if (error) throw error;
  return parseBillingRead(buildingSubscriptionSchema, data, "building subscription");
}

export async function changeBuildingPlan(
  orgId: string,
  buildingId: number,
  tier: BuildingPlanTier
) {
  const data = await invokeBilling("change_building_plan", orgId, {
    building_id: buildingId,
    tier,
  });
  return parseBillingRead(buildingPlanChangeSchema, data, "building plan change");
}

export async function getBillingContext(orgId: string) {
  const { data, error } = await supabase.rpc("get_organisation_billing_context", {
    p_org_id: orgId,
  });
  if (error) throw error;
  return parseBillingRead(billingContextSchema, data, "billing context");
}

export async function getBillingOverview(orgId: string) {
  const { data, error } = await supabase.rpc("get_organisation_billing_overview", {
    p_org_id: orgId,
  });
  if (error) throw error;
  return parseBillingRead(billingOverviewSchema, data, "billing overview");
}

export async function getBillingInvoices(orgId: string) {
  const { data, error } = await supabase.rpc("get_organisation_billing_invoices", {
    p_org_id: orgId,
  });
  if (error) throw error;
  return parseBillingRead(billingInvoicesSchema, data, "invoice list");
}

export async function saveBillingDetails(orgId: string, input: BillingDetailsInput) {
  await invokeBilling("save_details", orgId, {
    company_name: input.companyName,
    registry_code: input.registryCode,
    vat_number: input.vatNumber,
    legal_address: input.legalAddress,
    invoice_email: input.invoiceEmail,
    purchase_order_number: input.purchaseOrderNumber,
    payment_method: input.paymentMethod,
  });
}

/**
 * Open a Stripe Checkout session in setup mode and answer with its hosted URL.
 *
 * `return_url` is where Stripe sends the browser back to; the function appends
 * its own `billing_setup` and `session_id` parameters and validates the origin
 * against its allowlist, so a full absolute URL is what it expects.
 *
 * `locale` is the language the app is being shown in. The function maps it onto
 * Stripe's Checkout `locale` enum — `en` and `et` are both in it
 * (docs.stripe.com/api/checkout/sessions/create) — and falls back to Checkout's
 * own `auto` for anything else, so an unknown tag is never an error.
 */
export async function startPaymentMethodSetup(orgId: string, returnUrl: string, locale?: string) {
  const data = await invokeBilling("setup_payment_method", orgId, {
    return_url: returnUrl,
    billing_context: "building_plan",
    ...(locale ? { locale } : {}),
  });
  return billingSetupSessionSchema.parse(data).url;
}

/**
 * Apply a completed Checkout session to the mirror without waiting for Stripe's
 * webhook, so the page the customer lands back on is already correct.
 *
 * Idempotent by design: the webhook applies the same update, and whichever
 * arrives second is a no-op. The webhook remains the guarantee — Stripe's own
 * documentation is explicit that a success redirect may never be loaded — so
 * this is promptness, not fulfilment.
 */
export async function confirmPaymentMethodSetup(orgId: string, sessionId: string) {
  await invokeBilling("confirm_setup", orgId, {
    session_id: sessionId,
    billing_context: "building_plan",
  });
}

export async function addBillingBuilding(
  orgId: string,
  input: {
    address: string;
    cadastralCode?: string;
    tier?: BuildingPlanTier;
    name?: string;
    registryCode?: string | null;
    creationRequestId?: string;
  }
) {
  const cadastralCode = input.cadastralCode?.trim();
  const data = await invokeBilling("add_building", orgId, {
    address: input.address,
    ...(input.tier ? { tier: input.tier } : {}),
    ...(input.name ? { name: input.name } : {}),
    ...(input.registryCode ? { registry_code: input.registryCode } : {}),
    ...(input.creationRequestId ? { creation_request_id: input.creationRequestId } : {}),
    // The field may be left blank — omit it so the building keeps NULL instead
    // of storing "" as a cadastral code.
    ...(cadastralCode ? { cadastral_code: cadastralCode } : {}),
  });
  return billingBuildingResultSchema.parse(data);
}

export async function removeBillingBuilding(orgId: string, buildingId: number) {
  const data = await invokeBilling("remove_building", orgId, { building_id: buildingId });
  return billingBuildingResultSchema.parse(data);
}

export async function restoreBillingBuilding(orgId: string, buildingId: number) {
  const data = await invokeBilling("restore_building", orgId, { building_id: buildingId });
  return billingBuildingResultSchema.parse(data);
}

/**
 * Ask the billing function to re-derive this organisation's Stripe quantity, and
 * do not wait for the answer.
 *
 * WHY IT IS CALLED AT ALL. Buildings are created and archived from the Buildings
 * page by an ordinary PostgREST write, not through this domain. The database
 * trigger opens the service row and enqueues the organisation, so the money is
 * already correct — but nothing pushes it to Stripe until the daily sweep drains
 * the queue, and until then the billing overview and the subscription disagree
 * with what the user just did.
 *
 * WHY IT IS FIRE-AND-FORGET. The sweep is the guarantee; this is only promptness.
 * A slow or refused sync must never fail, delay or roll back the building write
 * that already succeeded — including the ordinary refusal an org_admin gets,
 * since every billing action but the portal is owner-only server-side.
 */
export function requestBillingSync(orgId: string): void {
  void invokeBilling("sync", orgId)
    // A building created or restored on the Buildings page changes what billing
    // says — the estimated total, the building list, and whether adding the NEXT
    // one is still allowed. Nothing in that flow is a billing mutation hook, so
    // the invalidation is issued here, through the module-level client that
    // exists for exactly this (a repository write flushing a react-query cache).
    // It runs after the sync so the overview is re-read against the quantity
    // Stripe now holds, and it runs on failure too: the database is already
    // correct whatever Stripe did, and the sweep will push the rest.
    .catch((error: unknown) => {
      // Not an error state for the user: the queue row survives and the daily
      // sweep pushes the same absolute quantity.
      capture(error instanceof Error ? error : new Error("Billing sync deferred"), {
        tags: { feature: "billing", stage: "sync" },
        fingerprint: ["billing", "sync"],
        context: { billing: { orgId } },
      });
    })
    .finally(() => {
      void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY, refetchType: "all" });
    });
}

export async function retryBuildingActivation(orgId: string, buildingId: number) {
  await invokeBilling("sync", orgId);
  return getBuildingSubscription(buildingId);
}

/**
 * Where to send the browser for an invoice PDF.
 *
 * The archived copy in the private `billing-invoices` bucket is the primary
 * path: it is org-scoped by RLS, the signed URL lasts 60 seconds, and it
 * outlives the Stripe object. Stripe's own URLs expire 30 days after the due
 * date, so `hosted_invoice_url` is only the stand-in for the window between
 * finalisation and the archive landing — and being an unauthenticated link, it
 * is never preferred over the signed one.
 */
export async function resolveInvoiceDownloadUrl(
  invoice: Pick<BillingInvoice, "pdf_storage_path" | "hosted_invoice_url">
): Promise<string | null> {
  if (invoice.pdf_storage_path) {
    const { data, error } = await supabase.storage
      .from("billing-invoices")
      .createSignedUrl(invoice.pdf_storage_path, 60, { download: true });
    // A signing call can answer `{ data: null, error: null }`; reading through it
    // would throw a TypeError past the caller's localised download-failure path.
    if (error || !data?.signedUrl) {
      throw error ?? new Error("The invoice PDF was not signed for download");
    }
    return data.signedUrl;
  }
  return invoice.hosted_invoice_url;
}
